import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Container from '../../components/Container';
import ComponentErrorBoundary from '../../components/ComponentErrorBoundary';
import SEO from '../../lib/seo';
import { getClientEmails, getClientEmailsByClient, createClientEmail, updateClientEmail, deleteClientEmail } from '../../lib/adminApi';

/**
 * Admin Client Emails Management Page
 * @returns {React.ReactElement} Client emails management interface
 */
const AdminClientEmails = () => {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    client_id: clientId || '',
    email: '',
    role: 'client'
  });

  useEffect(() => {
    loadData();
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      
      let emailsData;
      if (clientId) {
        // Load emails for specific client
        emailsData = await getClientEmailsByClient(clientId);
      } else {
        // Load all emails
        emailsData = await getClientEmails();
      }
      
      setEmails(emailsData);
    } catch (err) {
      console.error('Failed to load emails:', err);
      setError('Failed to load client emails');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmail) {
        // Update existing email
        await updateClientEmail(editingEmail.id, {
          email: formData.email,
          role: formData.role
        });
      } else {
        // Create new email
        await createClientEmail(formData);
      }
      
      // Reset form and reload
      setFormData({ client_id: clientId || '', email: '', role: 'client' });
      setShowCreateForm(false);
      setEditingEmail(null);
      await loadData();
    } catch (err) {
      console.error('Failed to save email:', err);
      setError('Failed to save email');
    }
  };

  const handleEdit = (email) => {
    setEditingEmail(email);
    setFormData({
      client_id: email.client_id,
      email: email.email,
      role: email.role
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Are you sure you want to delete this email?')) {
      return;
    }
    
    try {
      await deleteClientEmail(emailId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete email:', err);
      setError('Failed to delete email');
    }
  };

  const cancelEdit = () => {
    setEditingEmail(null);
    setShowCreateForm(false);
    setFormData({ client_id: clientId || '', email: '', role: 'client' });
  };


  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-blue-600">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto mb-4"></div>
            <p className="text-white">Loading client emails...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-blue-600">
      <SEO 
        title={`${clientId ? `Client Emails - ${clientId}` : 'All Client Emails'} | LSAS Admin Portal`}
        description={clientId ? 'Manage email addresses for this client' : 'Manage all client email addresses'}
        url={`/admin/client-emails${clientId ? `?client=${clientId}` : ''}`}
      />
      <Header />
      
      {/* Admin Header */}
      <section className="pt-40 pb-8 bg-brand-black text-white">
        <Container>
          <div className="flex items-center justify-between">
            <div>
              <nav className="text-sm mb-2">
                <Link to="/admin" className="text-gray-400 hover:text-white">Admin</Link>
                <span className="text-gray-400 mx-2">/</span>
                {clientId && (
                  <>
                    <Link to="/admin/clients" className="text-gray-400 hover:text-white">Clients</Link>
                    <span className="text-gray-400 mx-2">/</span>
                  </>
                )}
                <span className="text-white">Client Emails</span>
              </nav>
              <h1 className="text-3xl font-bold mb-2">
                {clientId ? `Client Emails - ${clientId}` : 'All Client Emails'}
              </h1>
              <p className="text-gray-300">
                {clientId ? 'Manage email addresses for this client' : 'Manage all client email addresses'}
              </p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin"
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                ← Back to Dashboard
              </Link>
              {clientId && (
                <Link
                  to="/admin/clients"
                  className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
                >
                  ← Back to Clients
                </Link>
              )}
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-4 py-2 bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700"
              >
                Add New Email
              </button>
            </div>
          </div>
        </Container>
      </section>
      
      <main className="flex-grow py-8">
        <Container>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">
              {editingEmail ? 'Edit Email' : 'Add New Email'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                    required
                    disabled={!!clientId} // Disable if filtering by client
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                  >
                    <option value="client">Client</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Only client role is available. Admin users must be added directly through the database.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="bg-brand-blue-600 text-white px-6 py-2 rounded-md hover:bg-brand-blue-700 transition-colors"
                >
                  {editingEmail ? 'Update Email' : 'Add Email'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Emails List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Client Emails ({emails.length})
            </h2>
          </div>
          
          {emails.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No client emails found.</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-4 text-blue-600 hover:text-blue-800"
              >
                Add the first email
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {emails.map((email) => (
                    <tr key={email.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {email.client_name || email.client_id}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {email.client_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{email.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          email.role === 'admin' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {email.role}
                          {email.role === 'admin' && (
                            <span className="ml-1 text-xs">(Legacy)</span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(email.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(email)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(email.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </Container>
      </main>
      
      <Footer />
    </div>
  );
};

export default function AdminClientEmailsWithErrorBoundary() {
  return (
    <ComponentErrorBoundary>
      <AdminClientEmails />
    </ComponentErrorBoundary>
  );
}
